// CartDrawer.jsx — drop into src/components/
import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import config from "../config"; // adjust to './config' if CartDrawer is in src/pages/

const { SHOP_UPLOADS_URL } = config;

function formatPrice(val) {
  if (val === null || val === undefined) return "R0.00";
  return "R" + Number(val).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* ── Single cart line ── */
function CartItem({ item, onUpdateQty, onRemove }) {
  const price = Number(item.sale_price || item.price) || 0;
  const lineTotal = price * item.qty;

  return (
    <div className="cd-item">
      <div className="cd-item__media">
        {item.image1 ? (
          <img
            src={`${SHOP_UPLOADS_URL}/${item.image1}`}
            alt={item.name}
            className="cd-item__img"
            loading="lazy"
          />
        ) : (
          <div className="cd-item__img-fallback" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <path d="M3 9l4-4 4 4 4-5 4 5" />
            </svg>
          </div>
        )}
      </div>

      <div className="cd-item__body">
        {item.brand && <p className="cd-item__brand">{item.brand}</p>}
        <p className="cd-item__name">{item.name}</p>
        {item.sku && <p className="cd-item__sku">SKU: {item.sku}</p>}

        <div className="cd-item__footer">
          <div className="cd-item__qty">
            <button
              className="cd-item__qty-btn"
              onClick={() => onUpdateQty(item.id, item.qty - 1)}
              aria-label="Decrease quantity"
            >−</button>
            <span className="cd-item__qty-val" aria-live="polite">{item.qty}</span>
            <button
              className="cd-item__qty-btn"
              onClick={() => onUpdateQty(item.id, item.qty + 1)}
              aria-label="Increase quantity"
              disabled={item.qty >= (item.quantity ?? 999)}
            >+</button>
          </div>

          <div className="cd-item__pricing">
            <span className="cd-item__line-total">{formatPrice(lineTotal)}</span>
            {item.qty > 1 && (
              <span className="cd-item__unit-price">{formatPrice(price)} each</span>
            )}
          </div>
        </div>
      </div>

      <button
        className="cd-item__remove"
        onClick={() => onRemove(item.id)}
        aria-label={`Remove ${item.name} from cart`}
        title="Remove"
      >
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M4 4l12 12M16 4L4 16" />
        </svg>
      </button>
    </div>
  );
}

/* ── Empty state ── */
function EmptyCart({ onClose }) {
  const navigate = useNavigate();
  return (
    <div className="cd-empty">
      <div className="cd-empty__icon" aria-hidden="true">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
          <circle cx="9" cy="21" r="1" />
          <circle cx="20" cy="21" r="1" />
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
        </svg>
      </div>
      <p className="cd-empty__title">Your cart is empty</p>
      <p className="cd-empty__sub">Browse our products and add something you love.</p>
      <button
        className="cd-btn cd-btn--primary"
        onClick={() => { onClose(); navigate("/shop"); }}
      >
        Browse products
      </button>
    </div>
  );
}

/* ════════════════════════════════════════════
   MAIN CartDrawer
   Props:
     isOpen           — bool
     onClose          — fn()
     cartItems        — array of { ...product, qty }
     onUpdateQty      — fn(productId, qty)
     onRemoveFromCart — fn(productId)
     onClearCart      — fn()
     isLoggedIn       — bool
     onOpenAuth       — fn(tab) — opens auth modal
   ════════════════════════════════════════════ */
export function CartDrawer({
  isOpen,
  onClose,
  cartItems = [],
  onUpdateQty,
  onRemoveFromCart,
  onClearCart,
  isLoggedIn,
  onOpenAuth,
}) {
  const navigate = useNavigate();

  /* Close on Escape */
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  /* Lock body scroll */
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const subtotal = cartItems.reduce((sum, item) => {
    return sum + Number(item.sale_price || item.price || 0) * item.qty;
  }, 0);

  const itemCount = cartItems.reduce((s, i) => s + i.qty, 0);

  const handleCheckout = useCallback(() => {
    if (!isLoggedIn) {
      onOpenAuth?.("login");
      return;
    }
    onClose();
    navigate("/checkout");
  }, [isLoggedIn, onOpenAuth, onClose, navigate]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`cd-backdrop ${isOpen ? "cd-backdrop--visible" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className={`cd-drawer ${isOpen ? "cd-drawer--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Shopping cart"
      >
        {/* Header */}
        <div className="cd-header">
          <div className="cd-header__title-row">
            <div className="cd-header__title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              <span>Cart</span>
              {itemCount > 0 && (
                <span className="cd-header__count">{itemCount}</span>
              )}
            </div>
            <button className="cd-close" onClick={onClose} aria-label="Close cart">
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M4 4l12 12M16 4L4 16" />
              </svg>
            </button>
          </div>

          {cartItems.length > 0 && (
            <button className="cd-clear" onClick={onClearCart}>
              Clear all
            </button>
          )}
        </div>

        {/* Items */}
        <div className="cd-items">
          {cartItems.length === 0 ? (
            <EmptyCart onClose={onClose} />
          ) : (
            <div className="cd-items__list">
              {cartItems.map((item) => (
                <CartItem
                  key={item.id}
                  item={item}
                  onUpdateQty={onUpdateQty}
                  onRemove={onRemoveFromCart}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="cd-footer">
            <div className="cd-summary">
              <div className="cd-summary__row">
                <span>Subtotal ({itemCount} item{itemCount !== 1 ? "s" : ""})</span>
                <span className="cd-summary__amount">{formatPrice(subtotal)}</span>
              </div>
              <p className="cd-summary__note">
                Shipping and taxes calculated at checkout
              </p>
            </div>

            <div className="cd-footer__actions">
              <button
                className="cd-btn cd-btn--primary"
                onClick={handleCheckout}
              >
                {isLoggedIn ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                    Proceed to checkout
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    Sign in to checkout
                  </>
                )}
              </button>
              <button
                className="cd-btn cd-btn--ghost"
                onClick={() => { onClose(); navigate("/shop"); }}
              >
                Continue shopping
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;600;700;800&display=swap');

        /* ── Backdrop ── */
        .cd-backdrop {
          position: fixed;
          inset: 0;
          z-index: 8000;
          background: rgba(1, 15, 50, 0.55);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
        }
        .cd-backdrop--visible {
          opacity: 1;
          pointer-events: auto;
        }

        /* ── Drawer ── */
        .cd-drawer {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          z-index: 8100;
          width: 420px;
          max-width: 100vw;
          background: #ffffff;
          display: flex;
          flex-direction: column;
          box-shadow: -8px 0 40px rgba(1, 15, 50, 0.18);
          transform: translateX(100%);
          transition: transform 0.32s cubic-bezier(0.32, 0, 0, 1);
          font-family: 'Roboto', sans-serif;
        }
        .cd-drawer--open {
          transform: translateX(0);
        }

        /* ── Header ── */
        .cd-header {
          padding: 20px 20px 14px;
          border-bottom: 1px solid #f0f3f8;
          flex-shrink: 0;
          background: #fff;
        }
        .cd-header__title-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .cd-header__title {
          display: flex;
          align-items: center;
          gap: 9px;
          font-size: 17px;
          font-weight: 800;
          color: #0f1c38;
          letter-spacing: -0.02em;
        }
        .cd-header__title svg {
          color: #013d97;
        }
        .cd-header__count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 22px;
          height: 22px;
          border-radius: 11px;
          background: #013d97;
          color: #fff;
          font-size: 11px;
          font-weight: 800;
          padding: 0 6px;
        }
        .cd-close {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: 1.5px solid #e8ecf3;
          background: #f7f9fc;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6b7a99;
          transition: all 0.15s ease;
        }
        .cd-close:hover {
          background: #fee2e2;
          border-color: #fecaca;
          color: #dc2626;
        }
        .cd-clear {
          margin-top: 8px;
          background: none;
          border: none;
          cursor: pointer;
          font-family: 'Roboto', sans-serif;
          font-size: 12px;
          font-weight: 700;
          color: #9ca3af;
          padding: 0;
          transition: color 0.15s;
        }
        .cd-clear:hover {
          color: #dc2626;
        }

        /* ── Items area ── */
        .cd-items {
          flex: 1;
          overflow-y: auto;
          padding: 6px 0;
          scrollbar-width: thin;
          scrollbar-color: #e2e8f0 transparent;
        }
        .cd-items__list {
          display: flex;
          flex-direction: column;
        }

        /* ── Cart item ── */
        .cd-item {
          display: flex;
          align-items: flex-start;
          gap: 13px;
          padding: 16px 20px;
          border-bottom: 1px solid #f5f7fb;
          position: relative;
          transition: background 0.15s;
        }
        .cd-item:hover {
          background: #fafbfd;
        }
        .cd-item:last-child {
          border-bottom: none;
        }

        .cd-item__media {
          width: 72px;
          height: 72px;
          border-radius: 10px;
          overflow: hidden;
          background: #f0f4fa;
          flex-shrink: 0;
          border: 1px solid #e8ecf3;
        }
        .cd-item__img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .cd-item__img-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #b8c2d8;
        }

        .cd-item__body {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 3px;
          padding-right: 24px;
        }
        .cd-item__brand {
          font-size: 10.5px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: #029add;
        }
        .cd-item__name {
          font-size: 13.5px;
          font-weight: 700;
          color: #0f1c38;
          line-height: 1.35;
        }
        .cd-item__sku {
          font-size: 11px;
          color: #9ca3af;
        }

        .cd-item__footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 8px;
          gap: 10px;
        }

        .cd-item__qty {
          display: flex;
          align-items: center;
          border: 1.5px solid #e2e8f0;
          border-radius: 8px;
          overflow: hidden;
        }
        .cd-item__qty-btn {
          width: 30px;
          height: 30px;
          border: none;
          background: #f7f9fc;
          cursor: pointer;
          font-size: 15px;
          font-weight: 700;
          color: #374151;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.15s;
        }
        .cd-item__qty-btn:hover:not(:disabled) {
          background: #e8edf6;
          color: #013d97;
        }
        .cd-item__qty-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .cd-item__qty-val {
          min-width: 30px;
          text-align: center;
          font-size: 13px;
          font-weight: 700;
          color: #0f1c38;
          padding: 0 4px;
        }

        .cd-item__pricing {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 1px;
        }
        .cd-item__line-total {
          font-size: 14px;
          font-weight: 800;
          color: #013d97;
        }
        .cd-item__unit-price {
          font-size: 11px;
          color: #9ca3af;
          font-weight: 500;
        }

        .cd-item__remove {
          position: absolute;
          top: 16px;
          right: 16px;
          width: 24px;
          height: 24px;
          border-radius: 6px;
          border: none;
          background: transparent;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #b8c2d8;
          transition: all 0.15s ease;
        }
        .cd-item__remove:hover {
          background: #fee2e2;
          color: #dc2626;
        }

        /* ── Empty ── */
        .cd-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 60px 32px;
          text-align: center;
        }
        .cd-empty__icon {
          width: 72px;
          height: 72px;
          border-radius: 50%;
          background: #f0f4fa;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #b8c2d8;
          margin-bottom: 4px;
        }
        .cd-empty__title {
          font-size: 16px;
          font-weight: 800;
          color: #0f1c38;
        }
        .cd-empty__sub {
          font-size: 13.5px;
          color: #8895b0;
          line-height: 1.5;
          max-width: 260px;
        }

        /* ── Footer ── */
        .cd-footer {
          border-top: 1px solid #f0f3f8;
          padding: 20px;
          background: #fff;
          flex-shrink: 0;
        }

        .cd-summary {
          margin-bottom: 16px;
        }
        .cd-summary__row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 15px;
          font-weight: 600;
          color: #374151;
          margin-bottom: 6px;
        }
        .cd-summary__amount {
          font-size: 20px;
          font-weight: 900;
          color: #013d97;
          letter-spacing: -0.02em;
        }
        .cd-summary__note {
          font-size: 11.5px;
          color: #9ca3af;
          line-height: 1.5;
        }

        .cd-footer__actions {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        /* ── Buttons ── */
        .cd-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 13px 20px;
          border-radius: 10px;
          border: none;
          cursor: pointer;
          font-family: 'Roboto', sans-serif;
          font-size: 14px;
          font-weight: 700;
          transition: all 0.18s ease;
          width: 100%;
          letter-spacing: -0.01em;
        }
        .cd-btn--primary {
          background: linear-gradient(135deg, #013d97 0%, #0255cc 100%);
          color: #ffffff;
          box-shadow: 0 2px 8px rgba(1, 61, 151, 0.28);
        }
        .cd-btn--primary:hover {
          background: linear-gradient(135deg, #012f77 0%, #0147aa 100%);
          box-shadow: 0 4px 16px rgba(1, 61, 151, 0.36);
          transform: translateY(-1px);
        }
        .cd-btn--primary:active {
          transform: translateY(0);
        }
        .cd-btn--ghost {
          background: #f0f4fa;
          color: #5a6a8a;
          border: 1.5px solid #e2e8f0;
        }
        .cd-btn--ghost:hover {
          background: #e4ebf5;
          color: #013d97;
          border-color: #c8d4e8;
        }

        /* ── Responsive ── */
        @media (max-width: 480px) {
          .cd-drawer {
            width: 100vw;
          }
        }

        /* ── Accessibility ── */
        .cd-drawer *:focus-visible {
          outline: 2px solid #029add;
          outline-offset: 2px;
        }

        @media (prefers-reduced-motion: reduce) {
          .cd-drawer,
          .cd-backdrop {
            transition-duration: 0ms !important;
          }
        }
      `}</style>
    </>
  );
}

export default CartDrawer;